'use client';

import { useHubData } from '../../_lib/use-hub-data';
import type {
  CapabilityMode,
  HunterNote,
  KnowledgeFill,
  LearnedArea,
  ThingBuilt,
} from '@/lib/hub/types';

const AREA_LABEL: Record<LearnedArea['area'], string> = {
  voice: 'Your voice',
  customers: 'Your customers',
  offers: 'Your offers',
  what_works: 'What works',
};

const MODE_LABEL: Record<CapabilityMode['mode'], string> = {
  auto: 'Hunter does this on its own',
  suggest: 'Hunter waits for your OK',
  human: 'You do this yourself',
};

const MODE_CAP_LABEL: Record<CapabilityMode['capability'], string> = {
  respond: 'Replying to messages',
  attract: 'Reaching out to leads',
  publish: 'Writing posts',
  sell: 'Selling',
  contacts: 'Managing contacts',
};

export function Project() {
  const fill = useHubData<KnowledgeFill>('/api/project/knowledge-fill');
  const learned = useHubData<LearnedArea[]>('/api/project/learned');
  const built = useHubData<ThingBuilt[]>('/api/project/things-built');
  const modes = useHubData<CapabilityMode[]>('/api/project/modes');
  const note = useHubData<HunterNote>('/api/hub/hunter-note?surface=project');

  const percent = fill.data?.percent ?? 0;
  const learnedAreas = learned.data ?? [];
  const builtItems = built.data ?? [];
  const modeRows = modes.data ?? [];

  return (
    <div className="hub-shell">
      <div className="hub-col-tabbed">
        <div className="hub-hero">
          <h1 className="t">What Hunter knows about your business.</h1>
          <p className="s">The more you tell it, the more it gets right.</p>
        </div>

        {/* Knowledge fill */}
        <div className="bs-card">
          <h2 className="bs-title">
            {percent >= 50 ? 'Hunter knows your business pretty well.' : 'Hunter is just getting to know your business.'}
          </h2>
          <div className="bs-bar-row">
            <div className="bs-bar" title={`${percent}%`}>
              <div className="bs-bar-fill" style={{ width: `${percent}%` }} />
            </div>
            <span className="bs-bar-note">getting better every day</span>
          </div>
          <p className="bs-body">
            The more you tell it, the more it gets right. The first month after setup is where most of
            the magic happens.
          </p>
        </div>

        {/* Learned */}
        <div className="h2-eyebrow"><span>What Hunter has learned</span></div>
        <div className="ki-grid">
          {(learnedAreas.length ? learnedAreas : []).map((a) => (
            <div key={a.area} className="ki-card">
              <div className="ki-name">{AREA_LABEL[a.area]}</div>
              <div className="ki-desc">{a.desc}</div>
              <div className={`ki-status${a.status === 'weak' ? ' weak' : ''}`}>
                {a.status === 'good' ? '✓  Looking good' : '+  Could be richer'}
              </div>
            </div>
          ))}
        </div>

        {/* Things built */}
        <div className="h2-eyebrow">
          <span>Things Hunter built for you</span>
          {builtItems.length > 0 ? <span className="right">{builtItems.length} things</span> : null}
        </div>
        {built.isLoading ? (
          <div className="hub-skeleton">Loading…</div>
        ) : builtItems.length === 0 ? (
          <div className="hub-empty">Nothing built yet. Hunter starts making things once it knows enough.</div>
        ) : (
          <div className="ala-grid">
            {builtItems.map((b) => (
              <div key={b.id} className="ala-card">
                <span className="ala-kind">{b.kind}</span>
                <div className="ala-name">{b.name}</div>
                <div className="ala-result">{b.result}</div>
              </div>
            ))}
          </div>
        )}

        {/* Modes (read-only in PR2) */}
        <div className="h2-eyebrow"><span>How much Hunter does on its own</span></div>
        <div className="mc-list">
          {(modeRows.length ? modeRows : []).map((m) => (
            <div key={m.capability} className="mc-row">
              <div className="mc-cap">{MODE_CAP_LABEL[m.capability]}</div>
              <div className={`mc-mode${m.mode === 'auto' ? ' auto' : ''}`}>{MODE_LABEL[m.mode]}</div>
              <button className="mc-change" title="Coming with Settings" disabled>
                Change
              </button>
            </div>
          ))}
        </div>

        {/* Why this matters (static) */}
        <div className="h2-eyebrow"><span>Why this matters</span></div>
        <div className="sc-block">
          <p className="sc-body">
            The longer you use Hunter, the more it knows about your business. People you’ve talked to,
            what works for you, what your customers like. That’s why we say Hunter becomes{' '}
            <em>part of your business</em>.
          </p>
        </div>

        {note.data ? (
          <div className="hn-foot">
            {note.data.em
              ? note.data.text.split('{em}').map((part, i, arr) => (
                  <span key={i}>{part}{i < arr.length - 1 ? <em>{note.data!.em}</em> : null}</span>
                ))
              : note.data.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
